// Unpublished Work © 2025-2026 Deere & Company.

import {IconAdd, IconDelete, IconEdit, IconGear, IconShown} from '@deere/icons';
import {Box} from '@mui/material';
import {MANAGE_WORKBOARDS, VIEW_WORKBOARDS} from 'Common/constants/business-activities';
import {WORKBOARD_TEMPLATES} from 'Common/constants/routes';
import InfoMessage from 'OnLabor/workboard/common/info-message';
import {TEMPLATE_TYPE, fetchWorkboardDataGrid} from 'OnLabor/workboard/utils/fetch-workboard-data-grid';
import React from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {deleteWorkboardTemplate} from 'Services/work-board-service';
import DataGridWrapper, {DEFAULT_DESKTOP_MAX_HEIGHT_OFFSET} from 'Src/ui-core/components/common/data-table/data-grid-wrapper';
import {TOAST_TYPE} from 'Src/ui-core/constants/toast-constants';
import {getPresentationUrl} from 'Src/ui-core/features/onlabor/workboards/presentation/utils/presentation-utils';
import {useNavBarActions} from 'Src/ui-core/react-hooks/use-navbar-actions';
import {useToast} from 'Src/ui-core/react-hooks/use-toast';
import {closeDialog, openDialog} from 'Src/ui-core/store/actions/dialogs';
import {redirect} from 'Src/ui-core/utils/redirect-utils';
import {replaceTranslationNames} from 'Src/ui-core/utils/translation-utils';
import PropTypes from 'Utils/prop-type-utils';
import {fetchEffectDataV2} from 'Utils/react-utils';
import dialogTypes from '../../common/dialog-types';
import moment from 'moment';

function getTemplatesDialogMessage(message, translations, isLowerCase = false) {
    return replaceTranslationNames(message, {
        '0': 1,
        '1': isLowerCase ? translations.TEMPLATE.toLowerCase() : translations.TEMPLATE
    });
}

function getNavBarActions(membershipId, selectedRowId, onDeleteTemplateClick, history) {
    return [
        {
            onClick: () => redirect(`${WORKBOARD_TEMPLATES}/new`, history),
            Icon: <IconAdd/>,
            title: 'ADD',
            requiredMyJdPermissions: MANAGE_WORKBOARDS
        },
        {
            disabled: selectedRowId === null,
            onClick: onDeleteTemplateClick,
            Icon: <IconDelete/>,
            title: 'DELETE',
            requiredMyJdPermissions: MANAGE_WORKBOARDS
        },
        {
            disabled: selectedRowId === null,
            onClick: () => redirect(`${WORKBOARD_TEMPLATES}/${selectedRowId}`, history),
            Icon: <IconEdit/>,
            title: 'EDIT',
            requiredMyJdPermissions: MANAGE_WORKBOARDS
        },
        {
            Icon: (
                <IconShown
                    style={{
                        height: '14px',
                        width: '14px'
                    }}
                    viewBox={'4 4 22 22'}
                />
            ),
            onClick: () => window.open(getPresentationUrl(membershipId), '_blank'),
            title: 'ONLINK_VIEW_WORKBOARD',
            variant: 'secondary',
            requiredMyJdPermissions: VIEW_WORKBOARDS
        },
        {
            Icon: <IconGear/>,
            onClick: () => redirect(`${WORKBOARD_TEMPLATES}/settings/labor/jobs`, history),
            title: 'SETTINGS',
            variant: 'secondary',
            requiredMyJdPermissions: MANAGE_WORKBOARDS
        }
    ];
}

function getFilterComponent(
    membershipId,
    translations,
    openConfirmation,
    closeConfirmation,
    onDelete,
    selectedRowId,
    history
) {
    const onDeleteTemplateClick = () => {
        openConfirmation({
            onCancel: closeConfirmation,
            showLoadingIcon: true,
            message: getTemplatesDialogMessage(
                translations.ONLINK_WORKBOARD_TEMPLATES_DRAFTS_CONFIRM_DELETE, translations, true),
            title: translations.ONLINK_DELETE_TEMPLATE,
            cancelTitle: translations.CANCEL,
            continueTitle: translations.CONFIRM_DELETE,
            async onContinue() {
                await onDelete();
                closeConfirmation();
            }
        });
    };

    useNavBarActions(
        getNavBarActions(membershipId, selectedRowId, onDeleteTemplateClick, history));
}
function WorkboardTemplates(props) {
    const {
        translations,
        membership,
        openConfirmation,
        closeConfirmation,
        history,
        disableNavbar = false,
        selectFromTemplate,
        maxHeightOffset = DEFAULT_DESKTOP_MAX_HEIGHT_OFFSET
    } = props;

    const {
        ONLINK_NO_TEMPLATES_TITLE,
        ONLINK_NO_TEMPLATES_BODY,
        ONLINK_NO_TEMPLATES_HELPER
    } = translations;

    const {addToast} = useToast();

    const [loading, setLoading] = React.useState(true);
    const [templates, setTemplates] = React.useState({
        columns: [],
        rows: []
    });
    const [selectedTemplateId, setSelectedTemplateId] = React.useState(() => null);

    async function fetchTemplates() {
        setSelectedTemplateId(null);
        setLoading(true);

        return fetchEffectDataV2(
            async ({signal}) => {
                const res = await fetchWorkboardDataGrid(
                    TEMPLATE_TYPE,
                    membership.membershipId,
                    translations,
                    signal
                );

                const formattedTemplates = res.rows.map((template) => ({
                    ...template,
                    lastModified: moment.utc(template.lastModified, moment.ISO_8601, true).local()
                }));

                setTemplates({
                    columns: res.columns,
                    rows: formattedTemplates
                });
            },
            {
                onError: () => {
                    addToast({
                        message: translations.REFRESH_BROWSER_AND_TRY_AGAIN,
                        title: translations.ERROR_OCCURRED_TITLE,
                        type: TOAST_TYPE.ERROR
                    });
                    setTemplates({
                        columns: [],
                        rows: []
                    });
                },
                onFinally: () => setLoading(false)
            }
        );
    }

    async function onDelete() {
        let title,
            message,
            type;

        try {
            await deleteWorkboardTemplate(selectedTemplateId);

            title = translations.SUCCESS;
            message = getTemplatesDialogMessage(
                translations.ONLINK_WORKBOARD_TEMPLATES_DRAFTS_SUCCESS_MESSAGE, translations).toLowerCase();
            type = TOAST_TYPE.SUCCESS;
        } catch {
            title = getTemplatesDialogMessage(
                translations.ONLINK_WORKBOARD_TEMPLATES_DRAFTS_ERROR_MESSAGE, translations);
            message = translations.REFRESH_BROWSER_AND_TRY_AGAIN;
            type = TOAST_TYPE.ERROR;
        }

        addToast({
            title,
            message,
            type
        });
        fetchTemplates();
    }

    const filterComponent = disableNavbar ? null : getFilterComponent(
        membership.membershipId,
        translations,
        openConfirmation,
        closeConfirmation,
        onDelete,
        selectedTemplateId,
        history);

    React.useEffect(() => {
        if (!membership.membershipId) {
            return;
        }

        fetchTemplates();
    }, [membership]);

    return (
        <Box
            sx={{
                m: '15px',
                flexGrow: 1,
                overflow: 'hidden'
            }}
        >
            {
                templates.rows.length === 0 && !loading ?
                    <InfoMessage
                        body={ONLINK_NO_TEMPLATES_BODY}
                        helper={ONLINK_NO_TEMPLATES_HELPER}
                        title={ONLINK_NO_TEMPLATES_TITLE}
                    /> :
                    <div className='data-grid'>
                        <DataGridWrapper
                            aggregation={{
                                name: {
                                    type: 'uniq',
                                    config: {
                                        prefix: `${replaceTranslationNames(translations.TOTAL_PLACEHOLDER, {
                                            '0': translations.ONLINK_TEMPLATES
                                        })}:`
                                    }
                                }
                            }}
                            columns={templates.columns}
                            filterComponent={filterComponent}
                            getRowId={(row) => row.workboardTemplateWorkboardId}
                            loading={loading}
                            maxHeightOffset={maxHeightOffset}
                            onRowSelectionModelChange={(selectionModel) => {
                                if (selectFromTemplate) {
                                    selectFromTemplate(selectionModel.ids.values().next().value);
                                }
                                setSelectedTemplateId(selectionModel.ids.values().next().value);
                            }}
                            pinnedColumns={{
                                left: [],
                                right: []
                            }}
                            rowSelectionMode='single'
                            rows={templates.rows}
                            searchFields={['name']}
                            searchable={true}
                            slotProps={{
                                root: {
                                    disableColumnMenu: true
                                }
                            }}
                            translations={translations}
                        />
                    </div>
            }
        </Box>
    );
}

WorkboardTemplates.propTypes = {
    closeConfirmation: PropTypes.func,
    disableNavbar: PropTypes.bool,
    history: PropTypes.history,
    maxHeightOffset: PropTypes.number,
    membership: PropTypes.membership,
    openConfirmation: PropTypes.func,
    selectFromTemplate: PropTypes.func,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations,
        membership: state.membership
    };
}

export function mapDispatchToProps(dispatch) {
    return {
        closeConfirmation() {
            dispatch(closeDialog(dialogTypes.CONFIRMATION_DIALOG));
        },
        openConfirmation(props) {
            dispatch(openDialog(dialogTypes.CONFIRMATION_DIALOG, props));
        }
    };
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(WorkboardTemplates));
